import React from "react";
import Link from "next/link";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupLabel,
  SidebarGroupLabelIcon,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import BoltOutlineIcon from "@src/lib/icons/18px/bolt-outline";
import FileSendOutlineIcon from "@src/lib/icons/18px/file-send-outline";
import AsteriskIcon from "@src/lib/icons/18px/asterisk";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/lib/ui/Collapsible";
import { useRouter } from "next/router";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import BoltSpeed2Icon from "@src/lib/icons/18px/bolt-speed-2";

const AdminLeftNavWorkflowsMenu: React.FC = () => {
  const { pathname } = useRouter();
  const { state } = useSidebar();
  const [isOpen, setIsOpen] = useBooleanLocalStorage(
    `admin-left-nav-workflows-menu-open`,
    true,
  );
  const { userActionPermissions } = useActionPermissions();
  const pathsAsWorkflows = useFeatureFlag(Ff.PathsAsWorkflows);
  const guestFeedbackEnabled = useFeatureFlag(Ff.GuestFeedbackCorrectiveAction);
  const guestFeedbackRecommendationsEnabled = useFeatureFlag(
    Ff.GuestFeedbackRecommendations,
  );
  return (
    <Collapsible
      open={state === "collapsed" ? true : isOpen}
      onOpenChange={setIsOpen}
      asChild
      className="group/collapsible"
    >
      <SidebarGroup>
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger>
            Workflows
            <SidebarGroupLabelIcon />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {userActionPermissions?.manageLibrary &&
                userActionPermissions.updateAutomations && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === "/automations"}
                      tooltip="Automations"
                    >
                      <Link href="/automations">
                        <BoltOutlineIcon />
                        Automations
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              {(!pathsAsWorkflows ||
                (userActionPermissions?.manageLibrary &&
                  userActionPermissions.updateAutomations)) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === "/paths"}
                    tooltip="Paths"
                  >
                    <Link href="/paths">
                      <AsteriskIcon />
                      Paths
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {userActionPermissions?.manageLibrary &&
                userActionPermissions.updateAutomations && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === "/access"}
                      tooltip="Access"
                    >
                      <Link href="/access">
                        <FileSendOutlineIcon />
                        Access
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              {userActionPermissions?.manageLibrary && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === "/knowledge-check"}
                    tooltip="Knowledge Check"
                  >
                    <Link href="/knowledge-check">
                      <BoltSpeed2Icon />
                      Knowledge Check
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {(guestFeedbackEnabled ||
                guestFeedbackRecommendationsEnabled) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.startsWith("/guest-feedback")}
                    tooltip="Guest Feedback"
                  >
                    <Link href="/guest-feedback">
                      <EmojiOutlineIcon />
                      Guest Feedback
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
};

export default AdminLeftNavWorkflowsMenu;
